import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CircleAlert, RefreshCw, ShieldCheck } from "lucide-react";
import { useLocation } from "react-router-dom";
import AlphaAccountContextList from "./alpha/AlphaAccountContextList";
import { useAuth } from "../contexts/AuthContext";
import { isAlphaDevPreviewFlow } from "../pages/alpha/alphaDevPreview";
import {
  trackAlphaCertificationVerifiedOnce,
  trackAlphaEvent,
} from "../pages/alpha/alphaAnalytics";
import agreementsService from "../services/agreementsService";
import type { HttpError } from "../services/httpService";
import { TrackingEvents } from "../services/trackingEvents";
import trackingService from "../services/trackingService";

export const INVESTOR_CERTIFICATION_AGREEMENT_NAME = "investor-certification";

/**
 * The 501(a) wording the share-page gate and this wall both show. One string,
 * so compliance copy cannot drift between the two places a person can certify.
 */
export const INVESTOR_CERTIFICATION_TEXT = "I certify that I have (or my organization has) sufficient financial sophistication and experience to evaluate hypothetical performance and its assumptions, risks, and limitations because I am an accredited investor (as such term is defined in Rule 501(a) under Regulation D).";

const getHttpStatus = (error: unknown): number | null => {
  const httpError = error as HttpError;
  return httpError.status ?? httpError.response?.status ?? null;
};

type CertificationStatus = "loading" | "unsigned" | "submitting" | "signed" | "error";

interface CertificationState {
  identityKey: string;
  status: CertificationStatus;
  submitError: string | null;
}

interface CertificationSessionValue {
  identityKey: string | null;
  state: CertificationState | null;
  verify: (force?: boolean, suppressAnalytics?: boolean) => Promise<void>;
  sign: (suppressAnalytics?: boolean) => Promise<void>;
  trackPendingCertificationSave: (actorUserId: string, save: Promise<unknown>) => Promise<void>;
}

const CertificationSessionContext = createContext<CertificationSessionValue | null>(null);

export function useCertificationSession(): CertificationSessionValue {
  const session = useContext(CertificationSessionContext);
  if (!session) {
    throw new Error("AccreditedInvestorGate requires InvestorCertificationProvider");
  }
  return session;
}

export const InvestorCertificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    effectiveUserId,
    isAuthenticated,
    isLoading,
    user,
  } = useAuth();
  const rawIdentity = !isLoading && isAuthenticated && user?.id && effectiveUserId
    ? JSON.stringify([user.id, effectiveUserId])
    : null;
  const previousRawIdentityRef = useRef(rawIdentity);
  const sessionGenerationRef = useRef(0);
  const requestIdRef = useRef(0);
  const pendingVerificationKeyRef = useRef<string | null>(null);
  const pendingSubmissionKeyRef = useRef<string | null>(null);
  const pendingCertificationSaveRef = useRef<{
    actorUserId: string;
    promise: Promise<unknown>;
  } | null>(null);

  if (previousRawIdentityRef.current !== rawIdentity) {
    previousRawIdentityRef.current = rawIdentity;
    sessionGenerationRef.current += 1;
    requestIdRef.current += 1;
    pendingVerificationKeyRef.current = null;
    pendingSubmissionKeyRef.current = null;
    if (!rawIdentity || pendingCertificationSaveRef.current?.actorUserId !== user?.id) {
      pendingCertificationSaveRef.current = null;
    }
  }

  const identityKey = rawIdentity
    ? JSON.stringify([
      sessionGenerationRef.current,
      rawIdentity,
      INVESTOR_CERTIFICATION_AGREEMENT_NAME,
    ])
    : null;
  const identityKeyRef = useRef(identityKey);
  identityKeyRef.current = identityKey;
  const actorUserIdRef = useRef(user?.id);
  actorUserIdRef.current = user?.id;
  const actorEmailRef = useRef(user?.email);
  actorEmailRef.current = user?.email;
  const effectiveUserIdRef = useRef(effectiveUserId);
  effectiveUserIdRef.current = effectiveUserId;
  const [storedState, setStoredState] = useState<CertificationState | null>(null);
  const storedStateRef = useRef(storedState);
  storedStateRef.current = storedState;

  useEffect(() => {
    if (!identityKey) setStoredState(null);
  }, [identityKey]);

  const trackPendingCertificationSave = useCallback(async (actorUserId: string, save: Promise<unknown>) => {
    const pendingSave = { actorUserId, promise: save };
    pendingCertificationSaveRef.current = pendingSave;
    try {
      await save;
    } finally {
      if (pendingCertificationSaveRef.current === pendingSave) {
        pendingCertificationSaveRef.current = null;
      }
    }
  }, []);

  const verify = useCallback(async (force = false, suppressAnalytics = false) => {
    const requestIdentityKey = identityKeyRef.current;
    if (!requestIdentityKey) return;
    const requestUserId = effectiveUserIdRef.current;

    const currentState = storedStateRef.current;
    if (
      !force
      && currentState?.identityKey === requestIdentityKey
      && currentState.status !== "loading"
    ) return;
    if (pendingVerificationKeyRef.current === requestIdentityKey) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    pendingVerificationKeyRef.current = requestIdentityKey;
    setStoredState({
      identityKey: requestIdentityKey,
      status: "loading",
      submitError: null,
    });

    try {
      const pendingSave = pendingCertificationSaveRef.current;
      if (pendingSave && pendingSave.actorUserId === actorUserIdRef.current) {
        // The share page can unmount while saving. Check the API only after the
        // save settles, including failures; the pending save never grants access.
        await pendingSave.promise.catch(() => undefined);
        if (
          identityKeyRef.current !== requestIdentityKey
          || requestIdRef.current !== requestId
        ) return;
      }
      const status = await agreementsService.getAgreementSignatureStatus(
        INVESTOR_CERTIFICATION_AGREEMENT_NAME
      );
      if (
        identityKeyRef.current === requestIdentityKey
        && requestIdRef.current === requestId
      ) {
        setStoredState({
          identityKey: requestIdentityKey,
          status,
          submitError: null,
        });
        if (status === "signed") {
          trackAlphaCertificationVerifiedOnce({
            isDevPreview: suppressAnalytics,
            userId: requestUserId,
          });
        }
      }
    } catch (error) {
      if (
        identityKeyRef.current === requestIdentityKey
        && requestIdRef.current === requestId
      ) {
        setStoredState({
          identityKey: requestIdentityKey,
          status: "error",
          submitError: null,
        });
        trackAlphaEvent(suppressAnalytics, TrackingEvents.ALPHA_CERTIFICATION_ERROR, {
          stage: "verification",
          status: getHttpStatus(error),
        });
      }
    } finally {
      if (
        pendingVerificationKeyRef.current === requestIdentityKey
        && requestIdRef.current === requestId
      ) {
        pendingVerificationKeyRef.current = null;
      }
    }
  }, []);

  const sign = useCallback(async (suppressAnalytics = false) => {
    const requestIdentityKey = identityKeyRef.current;
    if (!requestIdentityKey || pendingSubmissionKeyRef.current === requestIdentityKey) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    pendingSubmissionKeyRef.current = requestIdentityKey;
    setStoredState({
      identityKey: requestIdentityKey,
      status: "submitting",
      submitError: null,
    });

    try {
      await agreementsService.signAgreement({
        agreementName: INVESTOR_CERTIFICATION_AGREEMENT_NAME,
        data: {
          signedAt: new Date().toISOString(),
          userAgent: navigator.userAgent,
          email: actorEmailRef.current,
        },
      });
      if (
        identityKeyRef.current === requestIdentityKey
        && requestIdRef.current === requestId
      ) {
        setStoredState({
          identityKey: requestIdentityKey,
          status: "signed",
          submitError: null,
        });
        trackAlphaEvent(suppressAnalytics, TrackingEvents.ALPHA_CERTIFICATION_ACKNOWLEDGED, {
          result: "signed",
        });
      }
    } catch (error) {
      const status = getHttpStatus(error);
      if (
        identityKeyRef.current === requestIdentityKey
        && requestIdRef.current === requestId
      ) {
        if (status === 409) {
          setStoredState({
            identityKey: requestIdentityKey,
            status: "signed",
            submitError: null,
          });
          trackAlphaEvent(suppressAnalytics, TrackingEvents.ALPHA_CERTIFICATION_ACKNOWLEDGED, {
            result: "already_signed",
          });
        } else {
          setStoredState({
            identityKey: requestIdentityKey,
            status: "unsigned",
            submitError: error instanceof Error
              ? error.message
              : "Failed to record certification. Please try again.",
          });
          trackAlphaEvent(suppressAnalytics, TrackingEvents.ALPHA_CERTIFICATION_ERROR, {
            stage: "acknowledgement",
            status,
          });
        }
      }
    } finally {
      if (pendingSubmissionKeyRef.current === requestIdentityKey) {
        pendingSubmissionKeyRef.current = null;
      }
    }
  }, []);

  const state = identityKey && storedState?.identityKey === identityKey
    ? storedState
    : identityKey
      ? { identityKey, status: "loading" as const, submitError: null }
      : null;

  const value = useMemo<CertificationSessionValue>(() => ({
    identityKey,
    state,
    verify,
    sign,
    trackPendingCertificationSave,
  }), [identityKey, sign, state, trackPendingCertificationSave, verify]);

  return (
    <CertificationSessionContext.Provider value={value}>
      {children}
    </CertificationSessionContext.Provider>
  );
};

interface AccreditedInvestorGateProps {
  children: React.ReactNode;
  suppressConversionAnalytics?: boolean;
}

const AccreditedInvestorGate: React.FC<AccreditedInvestorGateProps> = ({ children, suppressConversionAnalytics = false }) => {
  const { identityKey, state, verify, sign } = useCertificationSession();
  const {
    accounts = [],
    actorUserId,
    effectiveUserId,
    isAccountContextLoading,
    isAccountSwitching,
    activateAccount,
  } = useAuth();
  const location = useLocation();
  const viewedIdentityKeyRef = useRef<string | null>(null);
  const normalizedPathname = location.pathname.toLowerCase().replace(/\/+$/, "");
  const suppressAnalytics = suppressConversionAnalytics || isAlphaDevPreviewFlow(
    location.state,
    normalizedPathname.endsWith("/dev-preview")
  );

  useEffect(() => {
    void verify(false, suppressAnalytics);
  }, [identityKey, suppressAnalytics, verify]);

  useEffect(() => {
    if (
      !identityKey
      || state?.status !== "unsigned"
      || suppressAnalytics
      || viewedIdentityKeyRef.current === identityKey
    ) return;

    viewedIdentityKeyRef.current = identityKey;
    trackingService.track(TrackingEvents.ALPHA_CERTIFICATION_VIEWED);
  }, [identityKey, state?.status, suppressAnalytics]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const hadDarkMode = root.classList.contains("dark");
    root.classList.remove("dark");

    return () => {
      if (hadDarkMode) root.classList.add("dark");
    };
  }, []);

  const accountSwitcher = accounts.length > 1 ? (
    <section
      aria-labelledby="alpha-certification-account-switcher-heading"
      className="w-full rounded-v7-panel border border-v7-ink/15 bg-v7-white p-4 shadow-lg"
    >
      <h2
        id="alpha-certification-account-switcher-heading"
        className="text-sm font-semibold text-v7-ink"
      >
        Switch account
      </h2>
      <p className="mt-1 text-xs leading-5 text-v7-ink-soft">
        Certification is checked separately for each account. Select another account to continue there.
      </p>
      <div className="mt-3">
        <AlphaAccountContextList
          accounts={accounts}
          actorUserId={actorUserId}
          effectiveUserId={effectiveUserId}
          isLoading={isAccountContextLoading}
          isSwitching={isAccountSwitching}
          onActivate={activateAccount}
        />
      </div>
    </section>
  ) : null;

  if (!state || state.status === "loading") {
    return (
      <div className="grid min-h-dvh place-items-center bg-v7-canvas p-4 font-v7-sans text-v7-ink">
        <div className="w-full max-w-xl space-y-6">
          <div role="status" className="flex items-center justify-center gap-3 text-sm text-v7-ink-soft">
            <span className="h-8 w-8 animate-spin rounded-full border-2 border-v7-ink/15 border-t-v7-bronze-dark" aria-hidden="true" />
            Checking investor certification
          </div>
          {accountSwitcher}
        </div>
      </div>
    );
  }

  if (state.status === "signed") {
    return <>{children}</>;
  }

  if (state.status === "error") {
    return (
      <div className="grid min-h-dvh place-items-center bg-v7-canvas p-4 font-v7-sans text-v7-ink">
        <div className="w-full max-w-xl space-y-4">
          <section role="alert" className="rounded-v7-panel border border-red-200 bg-red-50 p-6 text-center shadow-lg">
            <CircleAlert className="mx-auto h-7 w-7 text-red-700" aria-hidden="true" />
            <h1 className="mt-3 font-v7-serif text-3xl font-normal text-v7-ink">
              We could not verify your certification
            </h1>
            <p className="mt-2 text-sm text-red-700">
              Alpha stays locked until the certification service responds.
            </p>
            <button
              type="button"
              onClick={() => void verify(true, suppressAnalytics)}
              className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Retry
            </button>
          </section>
          {accountSwitcher}
        </div>
      </div>
    );
  }

  const submitting = state.status === "submitting";

  return (
    <div className="flex min-h-dvh items-center justify-center bg-v7-canvas p-4 font-v7-sans text-v7-ink">
        <div className="max-w-xl w-full space-y-4">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3">
              <img src="/favicon.png" alt="Plutus" className="h-14 w-14" />
              <img src="/logo.png" alt="Plutus" className="h-10 w-auto" />
            </div>
          </div>

          <div className="overflow-hidden rounded-v7-panel border border-v7-ink/15 bg-v7-white shadow-xl">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-center mb-4">
                <ShieldCheck className="h-7 w-7 text-v7-ink-soft" />
              </div>
              <h1 className="mb-6 text-center font-v7-serif text-3xl font-normal text-v7-ink">
                Investor Certification Required
              </h1>

              <p className="mb-5 text-sm leading-relaxed text-v7-ink-soft">
                Before viewing this content, please certify the following:
              </p>

              <div className="mb-6 rounded-v7-panel border border-v7-ink/15 bg-v7-paper p-5">
                <p className="text-sm leading-relaxed text-v7-ink">
                  {INVESTOR_CERTIFICATION_TEXT}
                </p>
              </div>

              {state.submitError && (
                <div className="mb-4 rounded-v7-control border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {state.submitError}
                </div>
              )}

              <button
                onClick={() => void sign(suppressAnalytics)}
                disabled={submitting}
                className="w-full rounded-v7-control bg-v7-ink px-6 py-3 font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Saving..." : "Agree and Continue"}
              </button>

              <p className="mt-4 text-center text-xs text-v7-ink/50">
                By clicking above, you acknowledge that you meet the criteria stated.
              </p>
            </div>
          </div>
          {accountSwitcher}
        </div>
    </div>
  );
};

export default AccreditedInvestorGate;
